import { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, Loader2, ArrowLeft } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { apiClient } from '@/lib/api-client';
import { storage } from '@/lib/storage';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { useVerification } from '@/contexts/VerificationContext';
import { motion, AnimatePresence } from 'framer-motion';

interface SearchGame {
  id: string;
  title: string;
  image: string;
  provider: string;
  aggregator?: string;
}

interface MobileSearchOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}

export const MobileSearchOverlay = ({ isOpen, onClose }: MobileSearchOverlayProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<SearchGame[]>([]);
  const [loading, setLoading] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { selectedCurrency } = useTheme();
  const { checkVerificationAndPrompt } = useVerification();

  // Focus input when overlay opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
      // Load recent searches from localStorage
      const saved = storage.getItem('recentGameSearches');
      if (saved) {
        try {
          setRecentSearches(JSON.parse(saved).slice(0, 5));
        } catch {
          setRecentSearches([]);
        }
      }
    } else {
      setSearchTerm('');
      setResults([]);
    }
  }, [isOpen]);

  // Prevent body scroll when overlay is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Search games when search term changes
  const searchGames = useCallback(async (term: string) => {
    if (!term.trim() || !user) {
      setResults([]);
      return;
    }

    setLoading(true);

    try {
      const response = await apiClient.post('/game/all', {
        excludeLive: false,
        page: 1,
        limit: 20,
        search: term.trim(),
      });

      if (response?.success && response.games) {
        const transformedGames: SearchGame[] = response.games.map((game: any) => {
          let gameIdPrefix = '';
          const aggregatorName = game.aggregator_name?.toLowerCase() || '';
          const providerName = game.provider_name?.toLowerCase() || '';

          if (aggregatorName === 'iconic21' || providerName === 'iconic21') {
            gameIdPrefix = 'iconic21-';
          } else if (aggregatorName === 'alea') {
            gameIdPrefix = 'alea-';
          } else if (aggregatorName === 'booming') {
            gameIdPrefix = 'booming-';
          } else if (aggregatorName === 'evoplay') {
            gameIdPrefix = 'evoplay-';
          }

          let imageUrl = '/placeholder.svg';
          if (game.use_custom_image && game.custom_image_url) {
            imageUrl = game.custom_image_url;
          } else if (game.thumbnail_url) {
            imageUrl = game.thumbnail_url;
          }

          return {
            id: `${gameIdPrefix}${game.external_game_id}`,
            title: game.game_name,
            image: imageUrl,
            provider: game.provider_name,
            aggregator: game.aggregator_name,
          };
        });

        setResults(transformedGames);
      } else {
        setResults([]);
      }
    } catch (error) {
      console.error('Error searching games:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchGames(searchTerm);
    }, 150);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, searchGames]);

  // Handle game click
  const handleGameClick = (game: SearchGame) => {
    // Check verification before allowing game launch
    if (!checkVerificationAndPrompt('data')) {
      return; // Blocked - verification modal shown
    }
    
    // Save to recent searches
    const newRecent = [game.title, ...recentSearches.filter(s => s !== game.title)].slice(0, 5);
    setRecentSearches(newRecent);
    storage.setItem('recentGameSearches', JSON.stringify(newRecent));
    
    navigate(`/play/${game.id}`);
    onClose();
  };

  // Handle recent search click
  const handleRecentClick = (term: string) => {
    setSearchTerm(term);
  };

  // Clear recent searches
  const clearRecentSearches = () => {
    setRecentSearches([]);
    storage.removeItem('recentGameSearches');
  };

  const themeColor = selectedCurrency === 'GC' ? 'amber' : 'emerald';

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[300] bg-background"
        >
          {/* Header */}
          <motion.div 
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.05, duration: 0.2 }}
            className={`sticky top-0 z-10 bg-card border-b border-border pt-[env(safe-area-inset-top)]`}
          >
            <div className="flex items-center gap-3 px-4 py-3">
              {/* Back button */}
              <button
                onClick={onClose}
                className={`flex items-center justify-center h-10 w-10 rounded-full transition-all ${
                  themeColor === 'amber' 
                    ? 'bg-amber-500/20 text-amber-400 active:bg-amber-500/30' 
                    : 'bg-emerald-500/20 text-emerald-400 active:bg-emerald-500/30'
                }`}
              >
                <ArrowLeft className="h-5 w-5" />
              </button>

              {/* Search Input */}
              <div className="flex-1 relative">
                <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${
                  themeColor === 'amber' ? 'text-amber-400/70' : 'text-emerald-400/70'
                }`} />
                <Input
                  ref={inputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search games..."
                  className={`pl-10 pr-10 h-11 bg-muted/80 border-border text-foreground placeholder:text-muted-foreground text-base rounded-xl ${
                    themeColor === 'amber'
                      ? 'focus:border-amber-500/50 focus:ring-amber-500/20'
                      : 'focus:border-emerald-500/50 focus:ring-emerald-500/20'
                  }`}
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground active:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          </motion.div>

          {/* Content */}
          <div className="h-[calc(100vh-70px)] overflow-y-auto overscroll-contain">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-20">
                <Loader2 className={`h-8 w-8 animate-spin ${
                  themeColor === 'amber' ? 'text-amber-400' : 'text-emerald-400'
                }`} />
                <span className="mt-3 text-muted-foreground text-sm">Searching games...</span>
              </div>
            ) : searchTerm.trim() ? (
              /* Search Results */
              results.length > 0 ? (
                <motion.div 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 space-y-2"
                >
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3 px-1">
                    {results.length} game{results.length !== 1 ? 's' : ''} found
                  </p>
                  {results.map((game, index) => (
                    <motion.button
                      key={game.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.03 }}
                      onClick={() => handleGameClick(game)}
                      className={`w-full flex items-center gap-4 p-3 rounded-xl transition-all active:scale-[0.98] ${
                        themeColor === 'amber'
                          ? 'bg-muted/50 active:bg-amber-900/30 border border-border'
                          : 'bg-muted/50 active:bg-emerald-900/30 border border-border'
                      }`}
                    >
                      {/* Game Thumbnail */}
                      <div className="w-16 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-muted shadow-lg">
                        <img
                          src={game.image}
                          alt={game.title}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = '/placeholder.svg';
                          }}
                        />
                      </div>
                      
                      {/* Game Info */}
                      <div className="flex-1 min-w-0 text-left">
                        <p className="text-foreground font-medium truncate text-base">{game.title}</p>
                        <p className="text-muted-foreground text-sm truncate mt-0.5">{game.provider}</p>
                      </div>

                      {/* Play Button */}
                      <div className={`flex-shrink-0 px-4 py-2 rounded-lg font-semibold text-sm ${
                        themeColor === 'amber'
                          ? 'bg-amber-500 text-black'
                          : 'bg-emerald-500 text-black'
                      }`}>
                        Play
                      </div>
                    </motion.button>
                  ))}
                </motion.div>
              ) : (
                <div className="flex flex-col items-center justify-center py-20 px-6">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
                    themeColor === 'amber' ? 'bg-amber-500/10' : 'bg-emerald-500/10'
                  }`}>
                    <Search className={`h-7 w-7 ${
                      themeColor === 'amber' ? 'text-amber-400/50' : 'text-emerald-400/50'
                    }`} />
                  </div>
                  <p className="text-muted-foreground text-center">
                    No games found for "<span className="text-foreground">{searchTerm}</span>"
                  </p>
                  <p className="text-muted-foreground/70 text-sm text-center mt-2">
                    Try a different search term
                  </p>
                </div>
              )
            ) : (
              /* Empty State / Recent Searches */
              <div className="p-4">
                {recentSearches.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="mb-6"
                  >
                    <div className="flex items-center justify-between mb-3 px-1">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">Recent Searches</p>
                      <button 
                        onClick={clearRecentSearches}
                        className="text-xs text-muted-foreground active:text-foreground"
                      >
                        Clear
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {recentSearches.map((term, index) => (
                        <button
                          key={index}
                          onClick={() => handleRecentClick(term)}
                          className={`px-4 py-2 rounded-full text-sm transition-all ${
                            themeColor === 'amber'
                              ? 'bg-amber-500/10 text-amber-300 border border-amber-500/20 active:bg-amber-500/20'
                              : 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/20 active:bg-emerald-500/20'
                          }`}
                        >
                          {term}
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}

                {/* Search Prompt */}
                <div className="flex flex-col items-center justify-center py-12">
                  <div className={`w-20 h-20 rounded-full flex items-center justify-center mb-4 ${
                    themeColor === 'amber' 
                      ? 'bg-gradient-to-br from-amber-500/20 to-yellow-500/10' 
                      : 'bg-gradient-to-br from-emerald-500/20 to-green-500/10'
                  }`}>
                    <Search className={`h-9 w-9 ${
                      themeColor === 'amber' ? 'text-amber-400' : 'text-emerald-400'
                    }`} />
                  </div>
                  <p className="text-foreground text-lg font-medium">Find Your Game</p>
                  <p className="text-muted-foreground text-sm text-center mt-2 max-w-[260px]">
                    Search by game name or provider to start playing instantly
                  </p>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};


